import Slide from './Slide';
import { Analytics } from '@plaudit/analytics';

export interface CarouselOptions {
	analyticsCategory?: string;
	carouselEl: HTMLElement;
	carouselClass: string;
	doIndicatorClick?: boolean;
	duration?: number;
	onChange?: Function;
	onNext?: Function;
	onPrev?: Function;
	onInit?: Function;
}

interface CarouselState {
	priorIndex: number;
	currentIndex: number;
}

export default abstract class CarouselAbstr {
	public slides: Slide[] = [];
	// public slides: Element[] = [];
	private _state: CarouselState[] = [{
		priorIndex: undefined,
		currentIndex: 0,
	}];

	private get previousState(): CarouselState {
		return this._state[this._state.length - 2];
	}
	protected get state(): CarouselState {
		return this._state[this._state.length - 1];
	}

	protected set state(newState: CarouselState) {
		this._state.push(newState);
	}

	protected carouselClass: string;

	public carouselEl: HTMLElement;
	protected carouselInnerEl: HTMLElement;
	protected carouselFrameEl: HTMLElement;
	protected carouselCounterEl: HTMLElement;

	protected controls: HTMLElement;
	protected controlNext: HTMLElement;
	protected controlPrev: HTMLElement;

	protected duration: number;
	protected timeout: number;

	protected doIndicatorClick: boolean;

	protected analytics: Analytics;
	protected analyticsCategory: string;

	protected eventLabels = {
		previous: 'Previous',
		next: 'Next',
		indicator: 'Indicator',
	};

	// NOTE: Empty function that options can override. An inexpensive way to avoid
	// having to test if function is defined from options
	protected onChange: Function = (fn: any) => fn;
	protected onNext: Function = (fn: any) => fn;
	protected onPrev: Function = (fn: any) => fn;
	protected onInit: Function = (fn: any) => fn;

	constructor(options: CarouselOptions) {
		this.carouselClass = options.carouselClass;
		this.carouselEl = options.carouselEl;

		if (!this.carouselEl) return;

		this.carouselInnerEl = this.carouselEl.getElementsByClassName(this.carouselClass + '__inner')[0] as HTMLElement;
		this.carouselFrameEl = this.carouselEl.getElementsByClassName(this.carouselClass + '__frame')[0] as HTMLElement;

		this.onChange = options.onChange || this.onChange;
		this.onNext = options.onNext || this.onNext;
		this.onPrev = options.onPrev || this.onPrev;
		this.onInit = options.onInit || this.onInit;

		this.duration = options.duration;
		this.doIndicatorClick = options.doIndicatorClick;

		this.analyticsCategory = options.analyticsCategory;
		this.analytics = this.analyticsCategory ? Analytics.getInstance() : undefined;
	}

	protected abstract init(): void;

	public activatePrevIndex(): void {
		const newIndex: number = this.prevIndex();
		this.activateByIndex(newIndex);

		this.onPrev();
	}
	public activateNextIndex(): void {
		const newIndex: number = this.nextIndex();
		this.activateByIndex(newIndex);

		this.onNext(newIndex);
	}

	/** Activates slide, indicator, and caption by selected index */
	// NOTE: Number of slides should always equal number of indicators.
	// Maybe should refactor to avoid this?
	public activateByIndex(newIndex: number): void {
		if (this.state.currentIndex === newIndex) return;

		// Remove existing outgoing classes. Pull into own method?
		this.slides.forEach((slide: Slide, index: number) => {
			if (index === newIndex) {
				slide.activate();
			} else if (slide.isActive) {
				slide.deactivate();
			} else if (slide.isOutgoing) {
				slide.clear();
			}
		});

		// Set new current index
		this.state = {
			priorIndex: this.state.currentIndex,
			currentIndex: newIndex,
		};

		this.onChange(newIndex);

		this.initAutoAdvance();
	}

	/** Remove all active/outgoing classes from slides. */
	public deactiveAll(): void {
		const outEl = this.carouselEl.getElementsByClassName('outgoing');
		for (let i: number = outEl.length - 1; i > -1; i--) {
			const el: Element = outEl[i] as Element;
			el.classList.remove('outgoing');
		}
		const activeEl = this.carouselEl.getElementsByClassName('active');
		for (let i: number = activeEl.length - 1; i > -1; i--) {
			const el: Element = activeEl[i] as Element;
			el.classList.remove('active');
		}

		this.state.currentIndex = undefined;
	}

	/** Get Next index. Loops around if at start */
	public prevIndex(): number {
		return this.state.currentIndex === 0 ? this.slides.length - 1 : this.state.currentIndex - 1;
	}

	/** Get next index. Loops around if at end */
	public nextIndex(): number {
		return this.state.currentIndex === this.slides.length - 1 ? 0 : this.state.currentIndex + 1;
	}

	/** Starts automatic progression if duration specified. */
	protected initAutoAdvance(): void {
		if (this.duration) {
			if (this.timeout) {
				clearTimeout(this.timeout);
			}

			this.timeout = window.setTimeout(() => {
				this.activateNextIndex();
			}, this.duration);
		}
	}

	protected buildControls(): HTMLElement {
		const controlsAbstr: Element = document.createElement('div');
		const controlsNextAbstr: Element = document.createElement('button');
		const controlsPrevAbstr: Element = document.createElement('button');

		controlsAbstr.className = `${this.carouselClass}__controls`;
		controlsNextAbstr.className = `${this.carouselClass}__control control--next`;
		controlsPrevAbstr.className = `${this.carouselClass}__control control--prev`;
		controlsPrevAbstr.addEventListener('click', (event: MouseEvent) => {
			this.activatePrevIndex();
			this.trackControlEvent(this.eventLabels.previous);
		});
		controlsNextAbstr.addEventListener('click', (event: MouseEvent) => {
			this.activateNextIndex();
			this.trackControlEvent(this.eventLabels.next);
		});

		const controlPrev = controlsAbstr.appendChild(controlsPrevAbstr) as HTMLElement;
		const controlNext = controlsAbstr.appendChild(controlsNextAbstr) as HTMLElement;

		return controlsAbstr as HTMLElement;
	}

	protected buildIndicators(activeIndex: number): Element {
		if (this.slides.length > 1) {
			const indicatorTrayAbstr: Element = document.createElement('div');
			indicatorTrayAbstr.classList.add(this.carouselClass + '__indicators');

			for (let i: number = 0; i < this.slides.length; i++) {
				const indicatorAbstr: Element = document.createElement(this.doIndicatorClick ? 'button': 'div');
				indicatorAbstr.classList.add(this.carouselClass + '__indicator');
				indicatorAbstr.classList.add(`${this.carouselClass}__indicator--${i + 1}`);
				if (i === activeIndex) {
					indicatorAbstr.classList.add('active');
				}

				const indicatorTextEl = document.createElement('span');
				indicatorTextEl.textContent = `${this.doIndicatorClick ? 'Activate' : ''} slide ${i + 1}`;
				indicatorAbstr.appendChild(indicatorTextEl);

				const indicator: Element = indicatorTrayAbstr.appendChild(indicatorAbstr);

				if (this.doIndicatorClick) {
					indicator.addEventListener('click', (event: MouseEvent) => {
						this.trackControlEvent(this.eventLabels.indicator);
						this.activateByIndex(i);
					});
				}
			}

			// NOTE: Needed for transition
			window.setTimeout(() => {
				this.carouselEl.classList.add(this.carouselClass + '--hasIndicators');
			}, 50);

			return indicatorTrayAbstr;
		}
	}

	protected trackControlEvent = (label: string) => {
		return this.analytics
			? this.analytics.trackEvent(this.analyticsCategory, 'Control Click', label)
			: null;
		}
}
